import { useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import compact from 'lodash/fp/compact';

import { DealType, Role, TalentBoardEventType, TalentBoardPersistedEventType } from '@bloomays-lib/types.shared';
import { LoaderGlobal } from '@bloomays-lib/ui.shared';
import { useAuthStore } from '@bloomays-lib/common.auth/browser';

import SideBar from '../molecules/SideBar';
import TalentBoard from '../organisms/TalentBoard';
import JobResume from '../organisms/JobResume';
import { useTalentBoardStore } from '../../stores/talentBoardStore';
import { useJobList } from '../../hooks/useJobList';
import useTalentsForTalentBoard from '../../hooks/useTalentsForTalentBoard';
import useTalentBoardStats from '../../hooks/useTalentBoardStats';
import { useTalentBoardEvents } from '../../hooks/useTalentBoardEvents';
import InvitationsDashboard from '../organisms/InvitationsDashboard';
import EventsModal from '../organisms/EventsModal';
import { Logger } from '../../services/service-logger';

const logger = Logger('Job');

const Job = (): React.ReactNode => {
  const { navState, shareModalOpen, setShareModalOpen, eventsModalOpen, setEventsModalOpen } = useTalentBoardStore();
  const { t } = useTranslation(['talentBoard']);
  const navigate = useNavigate();
  const { id: jobId } = useParams<{ id: string; talentId?: string }>();
  const { getJobById, loading: jobsLoading, jobs } = useJobList();
  const auth = useAuthStore((state) => state.auth);
  const isClient = auth?.user?.roles?.includes(Role.Client) || false;
  const { createTalentBoardEvent } = useTalentBoardEvents();

  const job = jobId ? getJobById(jobId) : null;

  const { talents, loading } = useTalentsForTalentBoard(jobId || '');
  const { stats } = useTalentBoardStats(jobId || '');

  useEffect(() => {
    if (!jobId || !job) {
      return;
    }

    logger.info('Traffic', {
      page: 'JobList',
      data: { jobId },
      evt: {
        name: TalentBoardEventType.TalentboardOpenJob,
      },
      job: {
        id: job.id,
        name: job.name,
      },
    });

    if (!isClient) {
      return;
    }

    void createTalentBoardEvent({
      jobId,
      date: new Date(),
      type: TalentBoardPersistedEventType.TalentboardOpenJob,
    });
  }, [jobId, job?.id]);

  const interlocuteurEmails = useMemo(
    () => compact([job?.interlocuteur1Email, job?.interlocuteur2Email]),
    [job?.interlocuteur1Email, job?.interlocuteur2Email],
  );

  if (jobsLoading || jobs === null) {
    return <LoaderGlobal waitingText={t('loadingJob')} />;
  }

  if (!jobId || !job) {
    void navigate('/talentboard/jobs');
    return null;
  }

  const isCDI = Boolean(job.dealType) && [DealType.CDI_PEOPLE, DealType.CDI_TECH].includes(job.dealType as DealType);

  const closeShareModal = () => {
    setShareModalOpen(false);
  };

  const closeEventsModal = () => {
    setEventsModalOpen(false);
  };

  return (
    <PageContainer>
      <SideBar email={job.accountManager?.email || ''} isCDI={isCDI} />
      <ContentContainer>
        {navState === 'talentboard' && <TalentBoard job={job} talents={talents} stats={stats} loading={loading} />}
        {navState === 'resume' && <JobResume job={job} />}
      </ContentContainer>
      {jobId && (
        <InvitationsDashboard
          jobId={jobId}
          open={shareModalOpen}
          onClose={closeShareModal}
          interlocuteurEmails={interlocuteurEmails}
        />
      )}
      {jobId && !isClient && <EventsModal jobId={jobId} open={eventsModalOpen} onClose={closeEventsModal} />}
    </PageContainer>
  );
};

const PageContainer = styled('div')(({ theme }) => ({
  height: 'calc(100vh - 80px)', // 100vh moins la hauteur du Header (80px)
  flex: 1,
  width: '100%',
  display: 'flex',
  backgroundColor: theme.palette.red[20],

  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const ContentContainer = styled('div')(({ theme }) => ({
  padding: '24px',
  width: '100%',
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    padding: '12px',
  },
}));

export default Job;
